(() => {
  const body = document.body;
  let lenisInstance = null;
  let smoothScrollInitialized = false;
  let smoothScrollAttempts = 0;

  const onReady = (callback) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback, { once: true });
    } else {
      callback();
    }
  };

  const setScrollLock = (locked) => {
    body.classList.toggle("gf-lock", locked);
    if (!lenisInstance) return;
    if (locked && lenisInstance.stop) lenisInstance.stop();
    if (!locked && lenisInstance.start) lenisInstance.start();
  };

  const initSmoothScroll = () => {
    if (smoothScrollInitialized) return;
    const touchDevice =
      window.matchMedia("(hover: none)").matches ||
      window.matchMedia("(pointer: coarse)").matches ||
      "ontouchstart" in window;

    if (touchDevice) {
      smoothScrollInitialized = true;
      return;
    }

    if (typeof Lenis === "undefined") {
      smoothScrollAttempts += 1;
      if (smoothScrollAttempts < 20) {
        window.setTimeout(initSmoothScroll, 150);
      }
      return;
    }

    smoothScrollInitialized = true;
    lenisInstance = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      orientation: "vertical",
    });

    const raf = (time) => {
      lenisInstance.raf(time);
      requestAnimationFrame(raf);
    };

    requestAnimationFrame(raf);
  };

  const wait = (duration) => new Promise((resolve) => window.setTimeout(resolve, duration));

  const waitForWindowLoad = () =>
    new Promise((resolve) => {
      if (document.readyState === "complete") {
        resolve();
      } else {
        window.addEventListener("load", resolve, { once: true });
      }
    });

  const initPreloader = () => {
    const loader = document.querySelector("[data-gf-loader]");
    if (!loader) {
      initSmoothScroll();
      return;
    }

    Promise.all([waitForWindowLoad(), wait(900)])
      .then(() => {
        loader.classList.add("is-ready");
        return wait(460);
      })
      .then(() => {
        loader.classList.add("is-complete");
        body.classList.remove("gf-loading");
        document.dispatchEvent(new CustomEvent("gf:loader:complete"));
        initSmoothScroll();
        window.setTimeout(() => loader.remove(), 640);
      });
  };

  const initSmoothAnchorLinks = () => {
    const getTarget = (link) => {
      const href = link.getAttribute("href");
      if (!href || !href.includes("#")) return null;

      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      if (url.pathname.replace(/\/$/, "") !== window.location.pathname.replace(/\/$/, "")) return null;
      if (!url.hash || url.hash === "#") return null;

      return document.getElementById(decodeURIComponent(url.hash.slice(1)));
    };

    const scrollToTarget = (target, hash) => {
      const header = document.querySelector(".gf-header");
      const offset = header ? header.getBoundingClientRect().height + 16 : 0;

      if (lenisInstance && lenisInstance.scrollTo) {
        lenisInstance.scrollTo(target, { offset: -offset });
      } else {
        const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      }

      history.pushState(null, "", hash);

      if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
      setTimeout(() => target.focus({ preventScroll: true }), 450);
    };

    document.querySelectorAll('a[href*="#"]').forEach((link) => {
      const target = getTarget(link);
      if (!target) return;

      link.addEventListener("click", (event) => {
        const nextTarget = getTarget(link);
        if (!nextTarget) return;

        event.preventDefault();
        const hash = new URL(link.getAttribute("href"), window.location.origin).hash;
        scrollToTarget(nextTarget, hash);
      });
    });
  };

  onReady(initPreloader);
  onReady(initSmoothAnchorLinks);

  const loadVideo = (video) => {
    if (!video || video.dataset.gfVideoLoaded === "true") return;
    video.querySelectorAll("source[data-src]").forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute("data-src");
    });
    video.dataset.gfVideoLoaded = "true";
    video.load();
    video.play().catch(() => {});
  };

  const initLazyVideos = () => {
    const videos = [...document.querySelectorAll("[data-gf-lazy-video]")];
    if (!videos.length) return;

    if (!("IntersectionObserver" in window)) {
      videos.forEach(loadVideo);
      return;
    }

    const videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadVideo(entry.target);
          videoObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "300px 0px" }
    );

    videos.forEach((video) => videoObserver.observe(video));
  };

  onReady(initLazyVideos);

  const newsletterDismissedKey = "grainify-newsletter-dismissed";
  const isNewsletterDismissed = () => {
    try {
      return window.localStorage.getItem(newsletterDismissedKey) === "true";
    } catch (error) {
      return false;
    }
  };

  const dismissNewsletter = () => {
    try {
      window.localStorage.setItem(newsletterDismissedKey, "true");
    } catch (error) {
      return;
    }
  };

  document.querySelectorAll("[data-gf-toggle]").forEach((button) => {
    button.addEventListener("click", () => {
      const target = document.querySelector(button.getAttribute("data-gf-toggle"));
      if (!target) return;
      const open = target.classList.toggle("is-open");
      button.setAttribute("aria-expanded", String(open));
      target.setAttribute("aria-hidden", String(!open));
      if (target.classList.contains("gf-mobile-panel")) {
        setScrollLock(open);
      }
    });
  });

  document.querySelectorAll("[data-gf-close]").forEach((button) => {
    button.addEventListener("click", () => {
      const target = document.querySelector(button.getAttribute("data-gf-close"));
      if (!target) return;
      if (target.id === "grainify-newsletter-modal") dismissNewsletter();
      target.classList.remove("is-open");
      target.setAttribute("aria-hidden", "true");
      setScrollLock(false);
    });
  });

  document.querySelectorAll("[data-gf-drawer-open]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      const drawer = document.querySelector(button.getAttribute("data-gf-drawer-open"));
      if (!drawer) return;
      document.querySelectorAll(".gf-mobile-panel.is-open").forEach((panel) => {
        panel.classList.remove("is-open");
        panel.setAttribute("aria-hidden", "true");
      });
      document.querySelectorAll("[data-gf-toggle][aria-expanded='true']").forEach((toggle) => toggle.setAttribute("aria-expanded", "false"));
      drawer.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
      setScrollLock(true);
    });
  });

  const openModal = (modal) => {
    if (!modal || modal.classList.contains("is-open")) return;
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    setScrollLock(true);
    const input = modal.querySelector("input[type='email']") || modal.querySelector("input") || modal.querySelector("button");
    if (input) window.setTimeout(() => input.focus(), 120);
  };

  document.querySelectorAll("[data-gf-modal-open]").forEach((button) => {
    button.addEventListener("click", () => {
      const modal = document.querySelector(button.getAttribute("data-gf-modal-open"));
      openModal(modal);
    });
  });

  const initNewsletterTrigger = () => {
    const modal = document.querySelector("#grainify-newsletter-modal");
    if (!modal) return;
    if (isNewsletterDismissed()) return;
    let shown = false;
    let fallbackTimer;

    const getScrollProgress = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return 0;
      return window.scrollY / scrollable;
    };

    const startsPastScrollTrigger = getScrollProgress() >= 0.35;

    const showNewsletter = () => {
      if (shown) return;
      if (isNewsletterDismissed()) return;
      shown = true;
      window.clearTimeout(fallbackTimer);
      window.removeEventListener("scroll", handleScroll);
      openModal(modal);
    };

    function handleScroll() {
      if (getScrollProgress() >= 0.35) showNewsletter();
    }

    fallbackTimer = window.setTimeout(showNewsletter, 30000);
    if (!startsPastScrollTrigger) {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }
  };

  if (body.classList.contains("gf-loading")) {
    document.addEventListener("gf:loader:complete", initNewsletterTrigger, { once: true });
  } else {
    initNewsletterTrigger();
  }

  document.querySelectorAll("[data-gf-thumb]").forEach((button) => {
    button.addEventListener("click", () => {
      const gallery = button.closest("[data-gf-gallery]");
      const main = gallery && gallery.querySelector("[data-gf-gallery-main]");
      const stage = gallery && gallery.querySelector("[data-gf-gallery-stage]");
      const img = button.querySelector("img");
      if (!main || !img) return;
      const mode = button.getAttribute("data-gf-gallery-mode") || "contain";
      const src = button.getAttribute("data-gf-gallery-src") || img.getAttribute("src");
      const srcset = button.getAttribute("data-gf-gallery-srcset");
      const sizes = button.getAttribute("data-gf-gallery-sizes");
      main.setAttribute("src", src);
      main.setAttribute("alt", img.getAttribute("alt") || "");
      if (srcset) main.setAttribute("srcset", srcset);
      if (sizes) main.setAttribute("sizes", sizes);
      main.setAttribute("data-gf-gallery-fit", mode);
      if (stage) {
        stage.style.setProperty("--gf-zoom-x", "50%");
        stage.style.setProperty("--gf-zoom-y", "50%");
      }
      gallery.querySelectorAll("[data-gf-thumb]").forEach((item) => item.classList.remove("is-active"));
      button.classList.add("is-active");
    });
  });

  document.querySelectorAll("[data-gf-gallery-stage]").forEach((stage) => {
    stage.addEventListener("pointermove", (event) => {
      if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
      const bounds = stage.getBoundingClientRect();
      const x = ((event.clientX - bounds.left) / bounds.width) * 100;
      const y = ((event.clientY - bounds.top) / bounds.height) * 100;
      stage.style.setProperty("--gf-zoom-x", `${Math.max(0, Math.min(100, x))}%`);
      stage.style.setProperty("--gf-zoom-y", `${Math.max(0, Math.min(100, y))}%`);
    });

    stage.addEventListener("pointerleave", () => {
      stage.style.setProperty("--gf-zoom-x", "50%");
      stage.style.setProperty("--gf-zoom-y", "50%");
    });
  });

  document.querySelectorAll("[data-gf-qty]").forEach((control) => {
    const input = control.querySelector("input");
    control.querySelectorAll("button").forEach((button) => {
      button.addEventListener("click", () => {
        if (!input) return;
        const current = Math.max(1, parseInt(input.value || "1", 10));
        const next = button.dataset.gfQtyAction === "minus" ? Math.max(1, current - 1) : current + 1;
        input.value = String(next);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });
  });

  document.querySelectorAll("[data-gf-product-form]").forEach((form) => {
    const qtyInput = form.querySelector("input[name='quantity']");
    const submit = form.querySelector("[data-gf-product-submit]");
    const variantSelect = form.querySelector("[data-gf-variant-select]");
    const singleVariant = form.querySelector("input[name='id'][data-gf-variant-price]");

    const formatMoney = (cents, fallback) => {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") return window.Shopify.formatMoney(cents);
      const amount = (cents / 100).toFixed(2);
      if (!fallback) return amount;
      return fallback.replace(/\d[\d,]*(?:\.\d+)?/, amount);
    };

    const getVariantData = () => {
      if (variantSelect) {
        const option = variantSelect.selectedOptions[0];
        return {
          price: parseInt(option.dataset.gfVariantPrice || "0", 10),
          money: option.dataset.gfVariantMoney || "",
          available: option.dataset.gfVariantAvailable === "true",
        };
      }

      if (singleVariant) {
        return {
          price: parseInt(singleVariant.dataset.gfVariantPrice || "0", 10),
          money: singleVariant.dataset.gfVariantMoney || "",
          available: singleVariant.dataset.gfVariantAvailable === "true",
        };
      }

      return { price: 0, money: "", available: true };
    };

    const updateSubmit = () => {
      if (!submit) return;
      const variant = getVariantData();
      const qty = Math.max(1, parseInt((qtyInput && qtyInput.value) || "1", 10));
      submit.disabled = !variant.available;
      submit.textContent = variant.available ? `Add to Cart — ${formatMoney(variant.price * qty, variant.money)}` : "Sold Out";
    };

    updateSubmit();
    if (qtyInput) qtyInput.addEventListener("change", updateSubmit);
    if (variantSelect) variantSelect.addEventListener("change", updateSubmit);
  });

  const revealElements = [...document.querySelectorAll("[data-gf-reveal]")];
  if (revealElements.length) {
    if ("IntersectionObserver" in window) {
      const revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-visible");
            revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.24, rootMargin: "0px 0px -8% 0px" }
      );

      revealElements.forEach((element) => revealObserver.observe(element));
    } else {
      revealElements.forEach((element) => element.classList.add("is-visible"));
    }
  }

  document.querySelectorAll("[data-gf-hero-carousel]").forEach((carousel) => {
    const slides = [...carousel.querySelectorAll("[data-gf-hero-slide]")];
    const cursor = carousel.querySelector("[data-gf-hero-cursor]");
    const hero = carousel.closest(".gf-hero") || carousel;
    if (slides.length < 2) return;
    let index = 0;
    let timer;
    let flipTimer;

    const updateTheme = () => {
      const activeSlide = slides[index];
      if (!activeSlide) return;
      carousel.closest(".gf-hero").style.setProperty("--gf-hero-ground", activeSlide.dataset.gfHeroGround || "");
      carousel.closest(".gf-hero").style.setProperty("--gf-hero-ink", activeSlide.dataset.gfHeroInk || "");
    };

    const show = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      carousel.classList.add("is-switching");
      window.setTimeout(() => carousel.classList.remove("is-switching"), 720);
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle("is-active", active);
        slide.classList.remove("is-back", "is-flipping");
        slide.querySelectorAll("[data-gf-hero-dot]").forEach((dot) => {
          dot.classList.toggle("is-active", Number(dot.dataset.gfHeroIndex) === index);
        });
      });
      updateTheme();
    };

    const start = () => {
      window.clearInterval(timer);
      timer = window.setInterval(() => show(index + 1), 5500);
    };

    carousel.querySelectorAll("[data-gf-hero-prev]").forEach((button) => {
      button.addEventListener("click", () => {
        show(index - 1);
        start();
      });
    });

    carousel.querySelectorAll("[data-gf-hero-next]").forEach((button) => {
      button.addEventListener("click", () => {
        show(index + 1);
        start();
      });
    });

    carousel.querySelectorAll("[data-gf-hero-dot]").forEach((dot) => {
      dot.addEventListener("click", () => {
        show(Number(dot.dataset.gfHeroIndex || "0"));
        start();
      });
    });

    hero.addEventListener("mouseenter", () => {
      window.clearInterval(timer);
      hero.classList.add("is-cursor-active");
      if (cursor) cursor.classList.add("is-visible");
    });

    hero.addEventListener("mousemove", (event) => {
      hero.classList.add("is-cursor-active");
      const slide = slides[index];
      if (!slide) return;
      const rect = hero.getBoundingClientRect();
      const progress = (event.clientX - rect.left) / rect.width;
      const back = progress < 0.5;
      const changed = slide.classList.contains("is-back") !== back;
      slide.classList.toggle("is-back", back);
      if (changed) {
        window.clearTimeout(flipTimer);
        slide.classList.add("is-flipping");
        flipTimer = window.setTimeout(() => slide.classList.remove("is-flipping"), 1040);
      }
      if (cursor) {
        cursor.style.left = `${event.clientX}px`;
        cursor.style.top = `${event.clientY}px`;
        cursor.textContent = back ? "Back ->" : "<- Front";
        cursor.classList.toggle("is-back", back);
      }
    });

    hero.addEventListener("mouseleave", () => {
      hero.classList.remove("is-cursor-active");
      if (cursor) cursor.classList.remove("is-visible");
      start();
    });

    updateTheme();
    if (body.classList.contains("gf-loading")) {
      document.addEventListener("gf:loader:complete", start, { once: true });
    } else {
      start();
    }
  });

  document.querySelectorAll("[data-gf-view-toggle]").forEach((toggle) => {
    const section = document.querySelector("[data-gf-collection-view]");
    if (!section) return;

    toggle.querySelectorAll("[data-gf-view]").forEach((button) => {
      button.addEventListener("click", () => {
        const list = button.dataset.gfView === "list";
        section.classList.toggle("is-list-view", list);
        toggle.querySelectorAll("[data-gf-view]").forEach((item) => {
          const active = item === button;
          item.classList.toggle("is-active", active);
          item.setAttribute("aria-pressed", String(active));
        });
      });
    });
  });

  let updateFixedMediaTargets = () => {};

  document.querySelectorAll("[data-gf-horizontal-showcase]").forEach((section) => {
    const track = section.querySelector("[data-gf-horizontal-track]");
    const progressEl = section.querySelector("[data-gf-horizontal-progress]");
    const currentEl = section.querySelector("[data-gf-horizontal-current]");
    const panels = track ? track.children.length : 0;

    const applyProgress = (progress) => {
      section.style.setProperty("--gf-horizontal-progress", String(progress));
      track.style.transform = `translate3d(${-progress * (panels - 1) * 100}vw, 0, 0)`;
      if (progressEl) progressEl.style.width = `${progress * 100}%`;
      if (currentEl) {
        const current = Math.min(panels, Math.floor(progress * panels) + 1);
        currentEl.textContent = String(current).padStart(2, "0");
      }
    };

    const update = () => {
      if (!track || panels < 2) return;
      if (window.matchMedia("(max-width: 1023px)").matches) {
        track.style.transform = "";
        if (progressEl) progressEl.style.width = "0";
        if (currentEl) currentEl.textContent = "01";
        return;
      }
      const rect = section.getBoundingClientRect();
      const scrollable = section.offsetHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const traveled = Math.min(Math.max(-rect.top, 0), scrollable);
      applyProgress(traveled / scrollable);
      updateFixedMediaTargets();
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
  });

  const fixedMedia = [
    ...document.querySelectorAll(
      ".gf-bleed__media img, .gf-bleed__media video, .gf-editorial-split__media img, .gf-horizontal__media img, [data-gf-parallax-bg] img"
    ),
  ].map((media) => ({
    media,
    x: 0,
    y: 0,
    scale: media.closest(".gf-horizontal__media, [data-gf-parallax-bg]") ? 1.08 : 1.18,
    targetX: 0,
    targetY: 0,
    targetScale: media.closest(".gf-horizontal__media, [data-gf-parallax-bg]") ? 1.08 : 1.18,
  }));

  if (fixedMedia.length > 0) {
    let frame;

    updateFixedMediaTargets = () => {
      const vh = window.innerHeight || 1;
      const vw = window.innerWidth || 1;

      fixedMedia.forEach((item) => {
        const holder = item.media.closest(".gf-horizontal__media, [data-gf-parallax-bg], .gf-bleed__media, .gf-editorial-split__media") || item.media;
        const rect = holder.getBoundingClientRect();
        const progress = Math.min(1, Math.max(0, (vh - rect.top) / (vh + rect.height)));

        if (item.media.closest(".gf-horizontal__media")) {
          const holderCenter = rect.left + rect.width / 2;
          item.targetX = (vw / 2 - holderCenter) * 0.16;
          item.targetY = (0.5 - progress) * 28;
          item.targetScale = 1.12;
        } else {
          item.targetX = 0;
          item.targetY = (0.5 - progress) * 120;
          item.targetScale = 1.18;
        }
      });

      if (!frame) {
        frame = window.requestAnimationFrame(renderFixedMedia);
      }
    };

    const renderFixedMedia = () => {
      let moving = false;

      fixedMedia.forEach((item) => {
        item.x += (item.targetX - item.x) * 0.1;
        item.y += (item.targetY - item.y) * 0.1;
        item.scale += (item.targetScale - item.scale) * 0.1;

        if (
          Math.abs(item.targetX - item.x) > 0.1 ||
          Math.abs(item.targetY - item.y) > 0.1 ||
          Math.abs(item.targetScale - item.scale) > 0.001
        ) {
          moving = true;
        }

        item.media.style.transform = `translate3d(${item.x}px, ${item.y}px, 0) scale(${item.scale})`;
      });

      if (moving) {
        frame = window.requestAnimationFrame(renderFixedMedia);
      } else {
        frame = undefined;
      }
    };

    updateFixedMediaTargets();
    window.addEventListener("scroll", updateFixedMediaTargets, { passive: true });
    window.addEventListener("resize", updateFixedMediaTargets);
  }

  window.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    document.querySelectorAll(".gf-cart-drawer.is-open, .gf-mobile-panel.is-open, .gf-modal.is-open").forEach((el) => {
      if (el.id === "grainify-newsletter-modal") dismissNewsletter();
      el.classList.remove("is-open");
      el.setAttribute("aria-hidden", "true");
    });
    document.querySelectorAll("[data-gf-toggle][aria-expanded='true']").forEach((button) => button.setAttribute("aria-expanded", "false"));
    setScrollLock(false);
  });
})();
